/**
 * CryptoWalletTemplate.tsx — Crypto Wallet Screening. Covers: crypto_wallet_screening.
 *
 * Same family as CompanyReportTemplate: a single AI-generated report object,
 * not a broker record list, so it needs its own layout rather than falling
 * through to PersonTemplate. Real shape (see the `ars_wallet_screening`
 * entity's one row):
 *
 *   { ok, report_url, screening_id, screened_at, query: { wallet_address,
 *     network, language, max_transactions },
 *     report: {
 *       screening_summary: { status, risk_level, risk_score, reason_codes[],
 *         manual_review_required, summary },
 *       wallet_profile: { address, networks[], activity_status,
 *         observed_assets[], labels[] },
 *       sanctions_screening: { status, direct_matches[], indirect_exposures[], summary },
 *       illicit_exposure[]: { category, exposure_type, hops,
 *         counterparty_address, title, details, severity, verification_status },
 *       transaction_patterns[]: (same family as illicit_exposure — shape not
 *         pinned down by any sample payload seen so far, so it's normalized
 *         with fallback field names rather than assumed),
 *       network_activity[]: { network, native_balance,
 *         transaction_count_observed, incoming_transfers, outgoing_transfers,
 *         unique_counterparties, first_activity, last_activity,
 *         observed_assets[], flow_summary, notable_transactions[]: { hash,
 *         date, direction, asset, amount, usd_value, counterparty, reason } },
 *       counterparty_analysis[]: { address, label, interactions,
 *         relationship, risk_level, risk_reason },
 *       attribution: { entity, entity_type, confidence, summary },
 *       public_intelligence[]: { title, date, status, details, verification_status },
 *       recommended_actions[]: string[],
 *       coverage: { networks_checked[], transactions_examined, period_start,
 *         period_end, checks_performed[], checks_unavailable[],
 *         limitations[], overall_confidence },
 *     } }
 *
 * Before this template existed, crypto_wallet_screening had no entry in
 * registry.ts's TYPE_MAP and fell through to PersonTemplate, which finds
 * nothing useful in this shape — the report was reachable only via RawCard's
 * collapsed JSON.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import {
  Chips, Cols, FieldTable, Flag, FlagTone, LinkPreviewCard, NoData, RawCard, ResCard, Sech, sentence,
  ShowMoreButton, Stack, Stat, Stats, useShowMore, useTokens, ValueList, Verdict, Warn,
} from '../ui/primitives'
import type { TemplateProps } from '../registry'

type ExposureItem = {
  title?: string
  category?: string
  details?: string
  severity?: string
  hops?: number
  counterpartyAddress?: string
  verificationStatus?: string
}

type NotableTx = {
  date?: string
  direction?: string
  asset?: string
  amount?: string
  usdValue?: number | null
  counterparty?: string
  reason?: string
}

type NetworkActivity = {
  network?: string
  nativeBalance?: string
  txCount?: number
  incoming?: number
  outgoing?: number
  uniqueCounterparties?: number
  firstActivity?: string
  lastActivity?: string
  observedAssets: string[]
  flowSummary?: string
  notableTx: NotableTx[]
}

type Counterparty = {
  address?: string
  label?: string
  interactions?: number
  relationship?: string
  riskLevel?: string
  riskReason?: string
}

type PublicIntelItem = { title?: string; date?: string; status?: string; details?: string; verificationStatus?: string }

type WalletReport = {
  ok: boolean
  reportUrl?: string
  screenedAt?: string
  walletAddress?: string
  network?: string
  language?: string

  screeningStatus?: string
  riskLevel?: string
  riskScore?: number
  reasonCodes: string[]
  manualReviewRequired: boolean
  screeningSummary?: string

  profileNetworks: string[]
  activityStatus?: string
  observedAssets: string[]
  labels: string[]

  sanctionsStatus?: string
  sanctionsDirectMatches: unknown[]
  sanctionsIndirectExposures: unknown[]
  sanctionsSummary?: string

  illicitExposure: ExposureItem[]
  transactionPatterns: ExposureItem[]
  networkActivity: NetworkActivity[]
  counterparties: Counterparty[]

  attributionEntity?: string
  attributionEntityType?: string
  attributionConfidence?: string
  attributionSummary?: string

  publicIntelligence: PublicIntelItem[]
  recommendedActions: string[]

  networksChecked: string[]
  transactionsExamined?: number
  periodStart?: string
  periodEnd?: string
  checksPerformed: string[]
  checksUnavailable: string[]
  limitations: string[]
  overallConfidence?: string
}

function normalizeIllicitExposure(raw: unknown): ExposureItem[] {
  return (Array.isArray(raw) ? raw : []).map((e: any) => ({
    title: e?.title || undefined,
    category: e?.category || undefined,
    details: e?.details || undefined,
    severity: e?.severity || undefined,
    hops: typeof e?.hops === 'number' ? e.hops : undefined,
    counterpartyAddress: e?.counterparty_address || undefined,
    verificationStatus: e?.verification_status || undefined,
  }))
}

/** transaction_patterns has no confirmed shape in any sample seen so far —
 * fall back through a few plausible field names rather than assuming one. */
function normalizeTransactionPatterns(raw: unknown): ExposureItem[] {
  return (Array.isArray(raw) ? raw : []).map((p: any) => ({
    title: p?.title || p?.pattern || p?.name || undefined,
    category: p?.category || p?.type || undefined,
    details: p?.details || p?.description || undefined,
    severity: p?.severity || undefined,
    hops: typeof p?.hops === 'number' ? p.hops : undefined,
    counterpartyAddress: p?.counterparty_address || p?.counterparty || undefined,
    verificationStatus: p?.verification_status || undefined,
  }))
}

function normalizeNetworkActivity(raw: unknown): NetworkActivity[] {
  return (Array.isArray(raw) ? raw : []).map((n: any) => ({
    network: n?.network || undefined,
    nativeBalance: n?.native_balance || undefined,
    txCount: typeof n?.transaction_count_observed === 'number' ? n.transaction_count_observed : undefined,
    incoming: typeof n?.incoming_transfers === 'number' ? n.incoming_transfers : undefined,
    outgoing: typeof n?.outgoing_transfers === 'number' ? n.outgoing_transfers : undefined,
    uniqueCounterparties: typeof n?.unique_counterparties === 'number' ? n.unique_counterparties : undefined,
    firstActivity: n?.first_activity || undefined,
    lastActivity: n?.last_activity || undefined,
    observedAssets: Array.isArray(n?.observed_assets) ? n.observed_assets : [],
    flowSummary: n?.flow_summary || undefined,
    notableTx: (Array.isArray(n?.notable_transactions) ? n.notable_transactions : []).map((t: any) => ({
      date: t?.date || undefined,
      direction: t?.direction || undefined,
      asset: t?.asset || undefined,
      amount: t?.amount || undefined,
      usdValue: typeof t?.usd_value === 'number' ? t.usd_value : null,
      counterparty: t?.counterparty || undefined,
      reason: t?.reason || undefined,
    })),
  }))
}

function normalizeCounterparties(raw: unknown): Counterparty[] {
  return (Array.isArray(raw) ? raw : []).map((c: any) => ({
    address: c?.address || undefined,
    label: c?.label || undefined,
    interactions: typeof c?.interactions === 'number' ? c.interactions : undefined,
    relationship: c?.relationship || undefined,
    riskLevel: c?.risk_level || undefined,
    riskReason: c?.risk_reason || undefined,
  }))
}

function normalizePublicIntel(raw: unknown): PublicIntelItem[] {
  return (Array.isArray(raw) ? raw : []).map((p: any) => ({
    title: p?.title || undefined,
    date: p?.date || undefined,
    status: p?.status || undefined,
    details: p?.details || undefined,
    verificationStatus: p?.verification_status || undefined,
  }))
}

function extract(apiData: any): WalletReport | null {
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  for (const e of entities) {
    const rows: any[] = Array.isArray(e?.data) ? e.data : []
    for (const row of rows) {
      const report = row?.report
      if (!report || typeof report !== 'object') continue

      const summary = report.screening_summary || {}
      const profile = report.wallet_profile || {}
      const sanctions = report.sanctions_screening || {}
      const attribution = report.attribution || {}
      const coverage = report.coverage || {}

      return {
        ok: row.ok !== false,
        reportUrl: row.report_url || undefined,
        screenedAt: row.screened_at || undefined,
        walletAddress: row.query?.wallet_address || profile.address || undefined,
        network: row.query?.network || undefined,
        language: row.query?.language || undefined,

        screeningStatus: summary.status || undefined,
        riskLevel: summary.risk_level || undefined,
        riskScore: typeof summary.risk_score === 'number' ? summary.risk_score : undefined,
        reasonCodes: Array.isArray(summary.reason_codes) ? summary.reason_codes : [],
        manualReviewRequired: !!summary.manual_review_required,
        screeningSummary: summary.summary || undefined,

        profileNetworks: Array.isArray(profile.networks) ? profile.networks : [],
        activityStatus: profile.activity_status || undefined,
        observedAssets: Array.isArray(profile.observed_assets) ? profile.observed_assets : [],
        labels: Array.isArray(profile.labels) ? profile.labels : [],

        sanctionsStatus: sanctions.status || undefined,
        sanctionsDirectMatches: Array.isArray(sanctions.direct_matches) ? sanctions.direct_matches : [],
        sanctionsIndirectExposures: Array.isArray(sanctions.indirect_exposures) ? sanctions.indirect_exposures : [],
        sanctionsSummary: sanctions.summary || undefined,

        illicitExposure: normalizeIllicitExposure(report.illicit_exposure),
        transactionPatterns: normalizeTransactionPatterns(report.transaction_patterns),
        networkActivity: normalizeNetworkActivity(report.network_activity),
        counterparties: normalizeCounterparties(report.counterparty_analysis),

        attributionEntity: attribution.entity || undefined,
        attributionEntityType: attribution.entity_type || undefined,
        attributionConfidence: attribution.confidence || undefined,
        attributionSummary: attribution.summary || undefined,

        publicIntelligence: normalizePublicIntel(report.public_intelligence),
        recommendedActions: Array.isArray(report.recommended_actions) ? report.recommended_actions : [],

        networksChecked: Array.isArray(coverage.networks_checked) ? coverage.networks_checked : [],
        transactionsExamined: typeof coverage.transactions_examined === 'number' ? coverage.transactions_examined : undefined,
        periodStart: coverage.period_start || undefined,
        periodEnd: coverage.period_end || undefined,
        checksPerformed: Array.isArray(coverage.checks_performed) ? coverage.checks_performed : [],
        checksUnavailable: Array.isArray(coverage.checks_unavailable) ? coverage.checks_unavailable : [],
        limitations: Array.isArray(coverage.limitations) ? coverage.limitations : [],
        overallConfidence: coverage.overall_confidence || undefined,
      }
    }
  }
  return null
}

/** DD.MM.YYYY — same convention as CompanyReportTemplate's fmtDate. */
function fmtDate(s?: string): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
}

/** DD.MM.YYYY HH:MM — transactions carry timestamps, not just dates. */
function fmtDateTime(s?: string): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function truncAddr(a?: string): string {
  if (!a) return '—'
  return a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a
}

function fmtAmount(a?: string): string {
  if (!a) return '0'
  const n = Number(a)
  if (Number.isNaN(n)) return a
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 })
}

function fmtUsd(v?: number | null): string {
  if (v === null || v === undefined) return '—'
  if (v > 0 && v < 0.01) return '<$0.01'
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

function riskTone(level?: string): 'ok' | 'warn' | 'bad' {
  const v = String(level || '').toLowerCase()
  if (v.startsWith('low') || v.startsWith('minimal') || v.startsWith('none')) return 'ok'
  if (v.startsWith('high') || v.startsWith('critical') || v.startsWith('severe')) return 'bad'
  return 'warn'
}

function statusTone(status?: string): 'ok' | 'warn' | 'bad' | undefined {
  const v = String(status || '').toLowerCase()
  if (!v) return undefined
  if (/clear|cleared|approved|no_action|pass/.test(v)) return 'ok'
  if (/flag|block|reject|denied/.test(v)) return 'bad'
  if (/review|pending/.test(v)) return 'warn'
  return undefined
}

function sanctionsTone(status?: string): 'ok' | 'warn' | 'bad' | undefined {
  const v = String(status || '').toLowerCase()
  if (!v) return undefined
  if (/no_public_match|no_match|clear/.test(v)) return 'ok'
  if (/partial|possible/.test(v)) return 'warn'
  if (/match|confirmed|sanctioned/.test(v)) return 'bad'
  return undefined
}

function severityFlagTone(severity?: string): FlagTone {
  const v = String(severity || '').toLowerCase()
  if (v.startsWith('low') || v.startsWith('minimal')) return 'ok'
  if (v.startsWith('high') || v.startsWith('critical') || v.startsWith('severe')) return 'bad'
  if (v.startsWith('moderate') || v.startsWith('medium')) return 'conflict'
  return 'primary'
}

function toFlagTone(t?: 'ok' | 'warn' | 'bad'): FlagTone {
  return t === 'ok' ? 'ok' : t === 'bad' ? 'bad' : t === 'warn' ? 'conflict' : 'primary'
}

function describeMatch(m: unknown): string {
  if (typeof m === 'string') return m
  if (m && typeof m === 'object') {
    const o = m as any
    return [o.name, o.entity, o.list, o.description, o.reason].filter(Boolean).join(' — ') || JSON.stringify(m)
  }
  return String(m)
}

/** Shared by Illicit exposure / Suspicious activity patterns — both are a
 * flagged-item-with-details shape, just sourced from different report keys. */
function ExposureList({ items }: { items: ExposureItem[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 6)
  return (
    <>
      {visible.map((e, i) => (
        <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 160 }}>
              {e.title || (e.category ? sentence(e.category) : 'Untitled')}
            </Typography>
            {e.severity && <Flag tone={severityFlagTone(e.severity)} label={sentence(e.severity) as React.ReactNode as string} />}
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[
              e.category ? sentence(e.category) as React.ReactNode as string : null,
              e.hops !== undefined ? `${e.hops} hop${e.hops === 1 ? '' : 's'}` : null,
              e.counterpartyAddress ? truncAddr(e.counterpartyAddress) : null,
              e.verificationStatus ? sentence(e.verificationStatus) as React.ReactNode as string : null,
            ].filter(Boolean).join(' · ') || ' '}
          </Typography>
          {e.details && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '5px', lineHeight: 1.5 }}>{e.details}</Typography>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

function TxList({ items }: { items: NotableTx[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 8)
  return (
    <>
      {visible.map((t, i) => (
        <Box key={i} sx={{ pt: i ? '9px' : 0, mt: i ? '9px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Box component="span" sx={{ fontSize: 11, fontWeight: 800, color: t.direction === 'in' ? '#10B981' : '#EF4444', flex: 'none' }}>
              {t.direction === 'in' ? '↓ IN' : '↑ OUT'}
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text }}>
              {fmtAmount(t.amount)} {t.asset}
            </Typography>
            <Typography sx={{ fontSize: 11, color: tk.text3, ml: 'auto' }}>{fmtUsd(t.usdValue)}</Typography>
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[fmtDateTime(t.date), t.counterparty ? truncAddr(t.counterparty) : null].filter(Boolean).join(' · ') || ' '}
          </Typography>
          {t.reason && (
            <Typography sx={{ fontSize: 11.5, color: tk.text2, mt: '3px', lineHeight: 1.4 }}>{t.reason}</Typography>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

function PublicIntelList({ items }: { items: PublicIntelItem[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 5)
  return (
    <>
      {visible.map((e, i) => (
        <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 160 }}>
              {e.title || 'Untitled'}
            </Typography>
            {e.status && <Flag tone="primary" label={sentence(e.status) as React.ReactNode as string} />}
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[fmtDate(e.date), e.verificationStatus ? sentence(e.verificationStatus) as React.ReactNode as string : null]
              .filter(Boolean).join(' · ') || ' '}
          </Typography>
          {e.details && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '5px', lineHeight: 1.5 }}>{e.details}</Typography>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

export function CryptoWalletTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const r = React.useMemo(() => extract(apiData), [apiData])

  if (!r) {
    return <NoData title="No wallet screening returned" hint="The screening engine produced no output for this address." />
  }

  const rTone = riskTone(r.riskLevel)
  const sTone = statusTone(r.screeningStatus)
  const sancTone = sanctionsTone(r.sanctionsStatus)

  const walletRows = ([
    ['Address', r.walletAddress],
    ['Network(s)', r.profileNetworks.length ? r.profileNetworks.join(', ') : r.network],
    ['Activity status', r.activityStatus ? sentence(r.activityStatus) : undefined],
    ['Language', r.language],
  ] as [string, React.ReactNode][]).filter(([, v]) => v)

  const attributionRows = ([
    ['Entity', r.attributionEntity],
    ['Entity type', r.attributionEntityType],
    ['Confidence', r.attributionConfidence ? sentence(r.attributionConfidence) : undefined],
  ] as [string, React.ReactNode][]).filter(([, v]) => v)
  const attributionHasContent = attributionRows.length > 0 || !!r.attributionSummary

  const coverageRows = ([
    ['Networks checked', r.networksChecked.length ? r.networksChecked.join(', ') : undefined],
    ['Transactions examined', r.transactionsExamined],
    ['Period', r.periodStart || r.periodEnd ? `${fmtDate(r.periodStart)} — ${fmtDate(r.periodEnd)}` : undefined],
    ['Overall confidence', r.overallConfidence ? sentence(r.overallConfidence) : undefined],
  ] as [string, React.ReactNode][]).filter(([, v]) => v)

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Wallet', value: truncAddr(r.walletAddress) },
            { label: 'Network', value: r.profileNetworks[0] || r.network || '—' },
            { label: 'Status', value: r.screeningStatus ? sentence(r.screeningStatus.replace(/_/g, ' ')) : '—', tone: sTone },
            { label: 'Risk level', value: r.riskLevel ? sentence(r.riskLevel) : '—', tone: r.riskLevel ? rTone : undefined },
            { label: 'Risk score', value: r.riskScore !== undefined ? `${r.riskScore}/100` : '—', tone: r.riskScore !== undefined ? rTone : undefined },
            { label: 'Activity', value: r.activityStatus ? sentence(r.activityStatus) : '—' },
          ]}
        />
      </ResCard>

      {!r.ok && (
        <ResCard>
          <Warn title="This screening may be incomplete">
            The screening engine flagged this run as unsuccessful — treat the sections below as partial.
          </Warn>
        </ResCard>
      )}

      {r.manualReviewRequired && (
        <ResCard>
          <Warn title="Manual review required">
            This wallet was flagged for manual review before any business decision is made.
          </Warn>
        </ResCard>
      )}

      {(r.riskLevel || r.screeningSummary) && (
        <ResCard title="Risk assessment" tone={rTone === 'ok' ? 'good' : rTone === 'bad' ? 'bad' : 'accent'}>
          <Verdict
            tone={rTone}
            title={(sentence(r.riskLevel || 'unknown') as React.ReactNode as string)}
            subtitle={r.riskScore !== undefined ? `score ${r.riskScore}/100` : undefined}
            icon={(
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3 4 6v6c0 5 3.4 9.2 8 10 4.6-.8 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" />
              </svg>
            )}
          />
          {r.screeningSummary && (
            <Typography sx={{ mt: '11px', fontSize: 12.5, lineHeight: 1.6, color: tk.text2 }}>{r.screeningSummary}</Typography>
          )}
          {r.reasonCodes.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.reasonCodes.length}>Reason codes</Sech>
              <Chips items={r.reasonCodes.map((c) => c.replace(/_/g, ' '))} tone="w" />
            </Box>
          )}
        </ResCard>
      )}

      <Cols n={2}>
        <Stack>
          <ResCard title="Wallet profile">
            <FieldTable rows={walletRows} />
            {r.observedAssets.length > 0 && (
              <Box sx={{ mt: '12px' }}>
                <Sech count={r.observedAssets.length}>Observed assets</Sech>
                <Chips items={r.observedAssets} tone="b" />
              </Box>
            )}
            {r.labels.length > 0 && (
              <Box sx={{ mt: '12px' }}>
                <Sech count={r.labels.length}>Labels</Sech>
                <Chips items={r.labels} />
              </Box>
            )}
          </ResCard>
        </Stack>

        <Stack>
          <ResCard title="Sanctions screening" tone={sancTone === 'bad' ? 'bad' : undefined}>
            {r.sanctionsStatus && (
              <Box sx={{ mb: '9px' }}>
                <Flag tone={toFlagTone(sancTone)} label={sentence(r.sanctionsStatus.replace(/_/g, ' ')) as React.ReactNode as string} />
              </Box>
            )}
            {r.sanctionsSummary && (
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: tk.text2 }}>{r.sanctionsSummary}</Typography>
            )}
            {r.sanctionsDirectMatches.length > 0 && (
              <Box sx={{ mt: '12px' }}>
                <Sech count={r.sanctionsDirectMatches.length}>Direct matches</Sech>
                <ValueList rows={r.sanctionsDirectMatches.map((m) => ({ value: describeMatch(m) }))} cap={5} />
              </Box>
            )}
            {r.sanctionsIndirectExposures.length > 0 && (
              <Box sx={{ mt: '12px' }}>
                <Sech count={r.sanctionsIndirectExposures.length}>Indirect exposures</Sech>
                <ValueList rows={r.sanctionsIndirectExposures.map((m) => ({ value: describeMatch(m) }))} cap={5} />
              </Box>
            )}
            {!r.sanctionsStatus && !r.sanctionsSummary && r.sanctionsDirectMatches.length === 0 && r.sanctionsIndirectExposures.length === 0 && (
              <Typography sx={{ fontSize: 12, color: tk.text3 }}>No sanctions data returned.</Typography>
            )}
          </ResCard>

          {r.reportUrl && (
            <ResCard title="Full report">
              <LinkPreviewCard
                url={r.reportUrl}
                title="Full wallet screening report (PDF)"
                description={r.screenedAt ? `Screened ${fmtDate(r.screenedAt)}` : undefined}
              />
            </ResCard>
          )}
        </Stack>
      </Cols>

      {(r.illicitExposure.length > 0 || r.transactionPatterns.length > 0) && (
        <Cols n={2}>
          <Stack>
            {r.illicitExposure.length > 0 ? (
              <ResCard title="Illicit exposure" count={r.illicitExposure.length}>
                <ExposureList items={r.illicitExposure} />
              </ResCard>
            ) : <Box />}
          </Stack>
          <Stack>
            {r.transactionPatterns.length > 0 ? (
              <ResCard title="Suspicious activity patterns" count={r.transactionPatterns.length}>
                <ExposureList items={r.transactionPatterns} />
              </ResCard>
            ) : <Box />}
          </Stack>
        </Cols>
      )}

      {r.networkActivity.map((n, i) => (
        <ResCard key={i} title={`Network activity${n.network ? ` — ${n.network}` : ''}`}>
          <Stats
            items={([
              n.nativeBalance ? { label: 'Native balance', value: fmtAmount(n.nativeBalance) } : null,
              n.txCount !== undefined ? { label: 'Transactions', value: n.txCount } : null,
              n.incoming !== undefined ? { label: 'Incoming', value: n.incoming } : null,
              n.outgoing !== undefined ? { label: 'Outgoing', value: n.outgoing } : null,
              n.uniqueCounterparties !== undefined ? { label: 'Counterparties', value: n.uniqueCounterparties } : null,
              n.firstActivity ? { label: 'First activity', value: fmtDate(n.firstActivity) } : null,
              n.lastActivity ? { label: 'Last activity', value: fmtDate(n.lastActivity) } : null,
            ].filter(Boolean) as Stat[])}
          />
          {n.observedAssets.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Chips items={n.observedAssets} tone="b" />
            </Box>
          )}
          {n.flowSummary && (
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: tk.text2, mt: '12px' }}>{n.flowSummary}</Typography>
          )}
          {n.notableTx.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={n.notableTx.length}>Notable transactions</Sech>
              <TxList items={n.notableTx} />
            </Box>
          )}
        </ResCard>
      ))}

      {r.counterparties.length > 0 && (
        <ResCard title="Counterparty analysis" count={r.counterparties.length}>
          <ValueList
            rows={r.counterparties.map((c) => ({
              value: c.label || truncAddr(c.address),
              note: [c.relationship, c.interactions !== undefined ? `${c.interactions} tx` : undefined, c.riskReason]
                .filter(Boolean).join(' · ') || undefined,
              flag: c.riskLevel && c.riskLevel !== 'unknown'
                ? { tone: toFlagTone(riskTone(c.riskLevel)), label: sentence(c.riskLevel) as React.ReactNode as string }
                : undefined,
            }))}
            cap={10}
          />
        </ResCard>
      )}

      {attributionHasContent && (
        <ResCard title="Attribution">
          {attributionRows.length > 0 ? (
            <FieldTable rows={attributionRows} />
          ) : (
            <Typography sx={{ fontSize: 12, color: tk.text3 }}>No verified public attribution was identified.</Typography>
          )}
          {r.attributionSummary && (
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: tk.text2, mt: attributionRows.length ? '10px' : 0 }}>
              {r.attributionSummary}
            </Typography>
          )}
        </ResCard>
      )}

      {r.publicIntelligence.length > 0 && (
        <ResCard title="Public intelligence" count={r.publicIntelligence.length}>
          <PublicIntelList items={r.publicIntelligence} />
        </ResCard>
      )}

      {r.recommendedActions.length > 0 && (
        <ResCard title="Recommended actions" count={r.recommendedActions.length}>
          <ValueList rows={r.recommendedActions.map((v) => ({ value: v }))} cap={10} />
        </ResCard>
      )}

      {(coverageRows.length > 0 || r.checksPerformed.length > 0 || r.checksUnavailable.length > 0 || r.limitations.length > 0) && (
        <ResCard title="Coverage">
          {coverageRows.length > 0 && <FieldTable rows={coverageRows} />}
          {r.checksPerformed.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.checksPerformed.length}>Checks performed</Sech>
              <Chips items={r.checksPerformed.map((c) => c.replace(/_/g, ' '))} tone="g" />
            </Box>
          )}
          {r.checksUnavailable.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.checksUnavailable.length}>Checks unavailable</Sech>
              <Chips items={r.checksUnavailable.map((c) => c.replace(/_/g, ' '))} />
            </Box>
          )}
          {r.limitations.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.limitations.length}>Limitations</Sech>
              <ValueList rows={r.limitations.map((v) => ({ value: v }))} cap={10} />
            </Box>
          )}
        </ResCard>
      )}

      <RawCard data={apiData} />
    </>
  )
}
